import React, { useState } from 'react';
import { X, CheckCircle, Loader2 } from 'lucide-react';
import StarRating from './StarRating';
import { submitReview } from '../../services/reviewService';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';

const ReviewSubmissionModal = ({
  isOpen,
  onClose,
  user,
  targetType,
  targetId,
  onSuccess
}) => {
  const [rating, setRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingExisting, setIsLoadingExisting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Fetch existing review if we open it
  React.useEffect(() => {
    if (isOpen && user && targetId) {
      const fetchExisting = async () => {
        setIsLoadingExisting(true);
        try {
          const userId = user.uid || user.id;
          const reviewId = `${userId}_${targetId}`;
          const reviewRef = doc(db, 'reviews', reviewId);
          const snap = await getDoc(reviewRef);
          if (snap.exists()) {
            const data = snap.data();
            setRating(data.rating || 0);
            setReviewText(data.reviewText || '');
          }
        } catch (err) {
          console.error("Error fetching existing review:", err);
        } finally {
          setIsLoadingExisting(false);
        }
      };
      fetchExisting();
    }
  }, [isOpen, user, targetId]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (rating === 0) {
      setError('يرجى اختيار تقييم (عدد النجوم) أولاً.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      await submitReview(user, targetType, targetId, rating, reviewText);
      setSuccess(true);
      if (onSuccess) {
        onSuccess({ rating, reviewText });
      }
      setTimeout(() => {
        setSuccess(false);
        onClose();
      }, 800);
    } catch (err) {
      setError(err.message || "حدث خطأ أثناء إرسال التقييم.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 rtl" dir="rtl">
      <div
        className="w-full max-w-lg bg-white dark:bg-[#0B0F19] dark:bg-opacity-80 dark:backdrop-blur-xl border border-gray-200 dark:border-white/10 rounded-2xl shadow-xl dark:shadow-[0_0_15px_rgba(218,165,32,0.1)] overflow-hidden transition-all transform scale-100 opacity-100"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-white/10">
          <h2 className="text-2xl font-bold tracking-wide text-gray-900 dark:text-white">
            شاركنا رأيك
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/10 rounded-xl transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 sm:p-8">
          {success ? (
            <div className="flex flex-col items-center justify-center py-8 text-center space-y-4">
              <CheckCircle size={64} className="text-amber-400" />
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">شكرًا لتقييمك!</h3>
              <p className="text-gray-600 dark:text-gray-300">لقد تم حفظ رأيك بنجاح، نقدر وقتك.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">

              {/* Star Rating Section */}
              <div className="flex flex-col items-center space-y-3">
                <span className="text-lg font-medium text-gray-600 dark:text-gray-300">
                  ما هو تقييمك العام؟
                </span>
                <StarRating rating={rating} onChange={setRating} size={36} />
              </div>

              {/* Error Message */}
              {error && (
                <div className="p-3 bg-red-100 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-red-600 dark:text-red-400 rounded-xl text-md text-center">
                  {error}
                </div>
              )}

              {/* Review Text Section */}
              <div className="space-y-2">
                <label className="block text-md font-medium text-gray-900 dark:text-gray-200">
                  تفاصيل التقييم (اختياري)
                </label>
                <textarea
                  rows={4}
                  placeholder="كيف كانت تجربتك؟ شاركنا التفاصيل..."
                  value={reviewText}
                  onChange={(e) => setReviewText(e.target.value)}
                  className="w-full p-4 rounded-xl resize-none transition-all outline-none bg-gray-50 border-gray-200 text-gray-900 focus:ring-2 focus:ring-gray-900/20 dark:bg-white/5 dark:border-white/10 dark:text-white dark:focus:ring-amber-400/50"
                ></textarea>
              </div>

              {/* Actions */}
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 text-lg font-bold text-gray-900 bg-gradient-to-r from-amber-200 to-amber-500 hover:from-amber-300 hover:to-amber-500 rounded-xl shadow-md hover:shadow-lg hover:scale-[1.02] transition-all disabled:opacity-70 disabled:hover:scale-100"
                >
                  {isSubmitting ? 'جاري الإرسال...' : 'إرسال التقييم'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReviewSubmissionModal;
